import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientNoteEntity } from './client-note.entity';
import { ClientEntity } from './client.entity';
import { EventClientEntity } from './event-client.entity';
import { MSmClientOpportunityStageEntity } from './m-sm-client-opportunity-stage.entity';
import type { OpportunityActivityEntity } from './opportunity-activity.entity';
import { OpportunityStageHistoryEntity } from './opportunity-stage-history.entity';
import type { OpportunityUserEntity } from './opportunity-user.entity';
import { OrderEntity } from './order.entity';
import { UserEntity } from './user.entity';

@Index('idx_opportunity_name_fulltext', ['mSmName'], { fulltext: true })
@Index(
	'm_sm_client_opportunity_m_sm_fk_order__id__deleted_userId',
	['deletedUserId', 'mSmFkOrderId'],
	{},
)
@Index('m_sm_fk_client__id', ['mSmFkClientId'], {})
@Index(
	'm_sm_fk_m_sm_client_opportunity_stage__id',
	['mSmFkMSmClientOpportunityStageId'],
	{},
)
@Index('m_sm_fk_order__id', ['mSmFkOrderId'], {})
@Index('m_sm_fk_user__id', ['mSmFkUserId'], {})
@Entity('m_sm_client_opportunity', {}, { label: 'Client Opportunity' })
export class MSmClientOpportunityEntity extends BaseEntity {
	@Column('varchar', { name: 'm_sm_name', length: 255 })
	public mSmName!: string;

	@Column('int', {
		name: 'm_sm_fk_m_sm_client_opportunity_stage__id',
		unsigned: true,
	})
	public mSmFkMSmClientOpportunityStageId!: number;

	@Column('int', { name: 'm_sm_ordinal', unsigned: true, default: () => "'0'" })
	public mSmOrdinal!: number;

	@Column('int', { name: 'm_sm_fk_client__id', unsigned: true })
	public mSmFkClientId!: number;

	@Column('int', { name: 'm_sm_fk_order__id', nullable: true, unsigned: true })
	public mSmFkOrderId!: number | null;

	@Column('decimal', {
		name: 'm_sm_amount',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public mSmAmount!: string | null;

	@Column('datetime', { name: 'm_sm_closeDate', nullable: true })
	public mSmCloseDate!: Date | null;

	@Column('int', { name: 'm_sm_fk_user__id', unsigned: true })
	public mSmFkUserId!: number;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(
		() => ClientNoteEntity,
		(clientNote) => clientNote.mSmFkMSmClientOpportunity,
		{},
	)
	public clientNotes!: Array<ClientNoteEntity>;

	@ManyToOne(
		() => MSmClientOpportunityStageEntity,
		(mSmClientOpportunityStage) =>
			mSmClientOpportunityStage.mSmClientOpportunities,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([
		{
			name: 'm_sm_fk_m_sm_client_opportunity_stage__id',
			referencedColumnName: 'id',
		},
	])
	public mSmFkMSmClientOpportunityStage!: MSmClientOpportunityStageEntity;

	@ManyToOne(() => ClientEntity, (client) => client.clientOpportunities, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'm_sm_fk_client__id', referencedColumnName: 'id' }])
	public mSmFkClient!: ClientEntity;

	@ManyToOne(() => OrderEntity, (order) => order.mSmClientOpportunities, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'm_sm_fk_order__id', referencedColumnName: 'id' }])
	public mSmFkOrder!: OrderEntity;

	@ManyToOne(() => UserEntity, (user) => user.mSmClientOpportunities, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'm_sm_fk_user__id', referencedColumnName: 'id' }])
	public mSmFkUser!: UserEntity;

	@OneToMany(
		() => EventClientEntity,
		(eventClient) => eventClient.fkMSmClientOpportunity,
		{},
	)
	public eventClients!: Array<EventClientEntity>;

	@OneToMany(
		() => OpportunityStageHistoryEntity,
		(stageHistory) => stageHistory.fkOpportunity,
		{},
	)
	public stageHistories!: Array<OpportunityStageHistoryEntity>;

	@OneToMany(
		'OpportunityActivityEntity',
		(activity: OpportunityActivityEntity) => activity.fkOpportunity,
		{},
	)
	public activities!: Array<OpportunityActivityEntity>;

	@OneToMany(
		'OpportunityUserEntity',
		(user: OpportunityUserEntity) => user.fkOpportunity,
		{},
	)
	public opportunityUsers!: Array<OpportunityUserEntity>;
}
